/**
 * Generic Usage Inventory Stepper
 * EXACT copy of Transfer UI but with 2 steps (Select Location → Configure Items)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, Box, Typography, IconButton, Stepper, Step, StepLabel, StepContent,
  Button, CircularProgress, useTheme, alpha, TextField, Alert
} from '@mui/material';
import {
  Close as CloseIcon, Remove as RemoveIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getUsageConfig } from '../usageConfigs';
import { useInventoryItems, useLocations } from '../../shared/useInventoryData';
import UsageItemsTable from './UsageItemsTable';
import LocationSelectDialog from './LocationSelectDialog';

export default function UsageInventoryStepper({
  open,
  onClose,
  onBack,
  inventoryType,
  companyId,
  onSuccess
}) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [location, setLocation] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [availableLots, setAvailableLots] = useState({});
  const [notes, setNotes] = useState('');

  // Search state (for location dialog)
  const [searchTerm, setSearchTerm] = useState('');
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  // Load data
  const { items } = useInventoryItems(inventoryType, companyId);
  const { locations } = useLocations(companyId);

  const isProduct = inventoryType === 'products';

  // Get config
  const config = React.useMemo(() => {
    try {
      return getUsageConfig(inventoryType);
    } catch (err) {
      console.error('Usage config error:', err);
      return null;
    }
  }, [inventoryType]);

  // Sort locations: primary (is_main) first, then by name - matches locations schema
  const sortedLocations = React.useMemo(() => {
    if (!locations) return [];
    return [...locations].sort((a, b) => {
      const aPrimary = !!(a.is_main || a.is_primary);
      const bPrimary = !!(b.is_main || b.is_primary);
      if (aPrimary && !bPrimary) return -1;
      if (!aPrimary && bPrimary) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [locations]);

  const steps = [
    'Select Location',
    'Configure Usage Items'
  ];

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setLocation(null);
      setSelectedItems({});
      setAvailableLots({});
      setNotes('');
      setError(null);
      setSearchTerm('');
      setLocationDialogOpen(false);
    }
  }, [open]);

  // Load available lots when transitioning to step 1
  useEffect(() => {
    if (location && items && items.length > 0 && activeStep === 1) {
      loadAvailableLots();
    }
  }, [location, items, activeStep]);

  const loadAvailableLots = async () => {
    if (!location) return;

    setLoading(true);
    try {
      const lotsData = {};
      for (const item of items) {
        const lots = await config.getInventoryItems(companyId, item.id, location.id);
        if (lots && lots.length > 0) {
          if (isProduct) {
            // Group by product+variant so each variant is a separate section
            for (const lot of lots) {
              const variantKey = lot.variant_id || 'null';
              const key = `${item.id}_${variantKey}`;
              if (!lotsData[key]) lotsData[key] = [];
              lotsData[key].push(lot);
            }
          } else {
            lotsData[item.id] = lots;
          }
        }
      }
      setAvailableLots(lotsData);
    } catch (err) {
      console.error('Error loading lots:', err);
      setError('Failed to load available inventory');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle quantity changes.
   * For chemicals: handleQuantityChange(key, lotId, value) — tier unused
   * For products:  handleQuantityChange(key, lotId, value, tier) — tier = 'pallets'|'cases'|'units'
   */
  const handleQuantityChange = (key, lotId, value, tier = 'quantity', mode = 'container') => {
    if (isProduct) {
      // Find the lot from availableLots[key]
      const lot = availableLots[key]?.find(l => l.id === lotId);
      if (!lot) return;

      const numValue    = parseInt(value) || 0;
      const upc         = lot.units_per_case   || 1;
      const cpp         = lot.cases_per_pallet || 1;
      const totalBudget = lot.total_base_units  || 0;

      // Current state of the OTHER two tiers so we know how much budget is left
      const cur = selectedItems[key]?.[lotId] || {};
      const curPallets = tier === 'pallets' ? 0 : (cur.pallets || 0);
      const curCases   = tier === 'cases'   ? 0 : (cur.cases   || 0);
      const curUnits   = tier === 'units'   ? 0 : (cur.units   || 0);

      let maxVal;
      if (tier === 'pallets') {
        maxVal = Math.max(0, Math.min(
          lot.pallet_quantity || 0,
          Math.floor(Math.max(0, totalBudget - curCases * upc - curUnits) / (cpp * upc))
        ));
      } else if (tier === 'cases') {
        maxVal = Math.max(0, Math.min(
          (lot.case_quantity || 0) + (lot.pallet_quantity || 0) * cpp,
          Math.floor(Math.max(0, totalBudget - curPallets * cpp * upc - curUnits) / upc)
        ));
      } else {
        maxVal = Math.max(0, totalBudget - curPallets * cpp * upc - curCases * upc);
      }

      const finalQty = Math.max(0, Math.min(numValue, maxVal));

      setSelectedItems(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          [lotId]: {
            ...(prev[key]?.[lotId] || { pallets: 0, cases: 0, units: 0 }),
            [tier]: finalQty,
            lot_number:   lot.lot_number,
            batch_number: lot.batch_number
          }
        }
      }));
    } else {
      // Chemicals — single quantity, supports container or measure mode
      const lot = availableLots[key]?.find(l => l.id === lotId);
      if (!lot) return;

      let finalQty, maxQty;
      if (mode === 'measure') {
        const numValue = parseFloat(value) || 0;
        maxQty   = lot.current_total_quantity || 0;
        finalQty = Math.max(0, Math.min(numValue, maxQty));
      } else {
        const numValue = parseInt(value) || 0;
        maxQty   = lot.current_quantity || 0;
        finalQty = Math.max(0, Math.min(numValue, maxQty));
      }

      setSelectedItems(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          [lotId]: {
            quantity:     finalQty,
            quantityMode: mode,
            lot_number:   lot.lot_number,
            batch_number: lot.batch_number,
            maxQuantity:  maxQty
          }
        }
      }));
    }
  };

  const getTotalUsageItems = () => {
    let count = 0;
    Object.values(selectedItems).forEach(lots => {
      Object.values(lots).forEach(lot => {
        if (isProduct) {
          if ((lot.pallets || 0) + (lot.cases || 0) + (lot.units || 0) > 0) count++;
        } else {
          if (lot.quantity > 0) count++;
        }
      });
    });
    return count;
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const usages = [];

      if (isProduct) {
        // key = 'productId_variantId'
        Object.keys(selectedItems).forEach(key => {
          const [itemId, variantPart] = key.split('_');
          const variantId = variantPart === 'null' ? null : variantPart;
          Object.keys(selectedItems[key]).forEach(lotId => {
            const lot = selectedItems[key][lotId];
            const totalTiers = (lot.pallets || 0) + (lot.cases || 0) + (lot.units || 0);
            if (totalTiers > 0) {
              usages.push({
                itemId,
                variantId,
                lotId,
                pallets:      lot.pallets      || 0,
                cases:        lot.cases        || 0,
                units:        lot.units        || 0,
                lot_number:   lot.lot_number,
                batch_number: lot.batch_number
              });
            }
          });
        });
      } else {
        Object.keys(selectedItems).forEach(itemId => {
          Object.keys(selectedItems[itemId]).forEach(lotId => {
            const lot = selectedItems[itemId][lotId];
            if (lot.quantity > 0) {
              usages.push({
                itemId,
                lotId,
                quantity:     lot.quantity,
                quantityMode: lot.quantityMode || 'container',
                lot_number:   lot.lot_number,
                batch_number: lot.batch_number
              });
            }
          });
        });
      }

      if (usages.length === 0) {
        setError('No items selected for usage');
        setLoading(false);
        return;
      }

      await config.executeUsage(companyId, {
        locationId: location.id,
        usages,
        notes
      });

      onSuccess?.(`Successfully recorded usage of ${usages.length} item(s) from ${location.name}`);
      onClose();
    } catch (err) {
      console.error('Usage error:', err);
      setError(err.message || 'Failed to record usage');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep0 = location !== null;
  const canProceedStep1 = getTotalUsageItems() > 0;

  if (!config) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)',
          boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <RemoveIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
              Record Usage - {config.displayName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Deduct inventory used in production
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <IconButton onClick={onBack} sx={{
              color: theme.palette.text.secondary,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04), transform: 'scale(1.05)' },
              transition: 'all 0.2s ease'
            }}>
              <ArrowBackIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.text.secondary,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 0, maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 0: Select Location */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {steps[0]}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Select the location where inventory will be deducted from
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setLocationDialogOpen(true)}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 2,
                    px: 2,
                    minHeight: 56,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    color: location ? 'text.primary' : 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    mb: 3,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)'
                    }
                  }}
                >
                  {location ? location.name : 'Select Location'}
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canProceedStep0}
                    sx={{
                      borderRadius: 2,
                      px: 5,
                      py: 1.8,
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  >
                    Continue
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Step 1: Configure Usage Items */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {steps[1]}
                </Typography>
              </StepLabel>
              <StepContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                    )}

                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                      Recording usage from: <strong style={{ fontWeight: 500 }}>{location?.name}</strong>
                    </Typography>

                    {Object.keys(availableLots).length === 0 ? (
                      <Alert severity="warning">
                        No inventory available at {location?.name}
                      </Alert>
                    ) : (
                      <UsageItemsTable
                        availableLots={availableLots}
                        items={items}
                        selectedItems={selectedItems}
                        onQuantityChange={handleQuantityChange}
                        isChemical={inventoryType === 'chemicals'}
                        isProduct={isProduct}
                      />
                    )}

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Notes (Optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      sx={{
                        mt: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'white'
                        }
                      }}
                    />

                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                      <Button
                        onClick={handleBack}
                        sx={{
                          borderRadius: 2,
                          px: 4,
                          py: 1.8,
                          fontSize: '1rem'
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || !canProceedStep1}
                        sx={{
                          borderRadius: 2,
                          px: 5,
                          py: 1.8,
                          fontWeight: 500,
                          fontSize: '1rem'
                        }}
                      >
                        {loading ? 'Processing...' : 'Submit Usage'}
                      </Button>
                    </Box>
                  </>
                )}
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </Box>

      {/* Location Select Dialog */}
      <LocationSelectDialog
        open={locationDialogOpen}
        onClose={() => {
          setLocationDialogOpen(false);
          setSearchTerm('');
        }}
        title="Select Location"
        locations={sortedLocations}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSelect={(loc) => {
          setLocation(loc);
          setLocationDialogOpen(false);
          setSearchTerm('');
        }}
      />
    </Dialog>
  );
}
